import { DOCUMENT } from '@angular/common';
import { inject, Inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from '../http-services/users.service';
import { AuthService } from './auth.service';
import { APP_TOKEN, AppsEnum } from '@itb-monorepo/enums';
import { CookieService } from 'ngx-cookie-service';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  currentLang?: string;
  languages = ['fa', 'en', 'ar', 'tr'];

  private readonly direction$ = new BehaviorSubject<'rtl' | 'ltr'>('ltr');

  private readonly currentLang$ = new BehaviorSubject<string>('en');

  private readonly usersService = inject(UsersService);
  private readonly authService = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly cookieService = inject(CookieService);

  public readonly app = inject(APP_TOKEN);

  constructor(
    @Inject(DOCUMENT)
    private readonly doc: Document
  ) {
    if (this.app === AppsEnum.SUPPORTER) {
      this.languages = ['fa', 'en'];
    }

    if (this.app === AppsEnum.CORPORATE) {
      this.setCurrentLanguage('en', false);
      return;
    }

    const urlLanguage = this.getLanguageFromUrl(this.doc.location.href);

    if (urlLanguage) {
      this.setCurrentLanguage(urlLanguage, false);
      return;
    }

    const cookieLanguage = this.normalizeLanguage(
      this.cookieService.get('language')
    );

    if (cookieLanguage) {
      this.setCurrentLanguage(cookieLanguage, false);
      return;
    }

    const browserLanguages = navigator.languages || [navigator.language];

    const matchedLanguage = browserLanguages
      .map((language) => this.normalizeLanguage(language))
      .find((language): language is string => Boolean(language));

    this.setCurrentLanguage(matchedLanguage ?? 'en', false);
  }
  syncLanguageFromUrl(url: string, persistOnServer = true): void {
    const language = this.getLanguageFromUrl(url);

    if (!language) {
      return;
    }

    this.setCurrentLanguage(language, persistOnServer);
  }

  setCurrentLanguage(lang: string, persistOnServer = true): void {
    const normalizedLanguage = this.normalizeLanguage(lang);

    if (!normalizedLanguage) {
      return;
    }

    const languageChanged = this.currentLang !== normalizedLanguage;

    if (!languageChanged) {
      return;
    }

    this.applyLanguage(normalizedLanguage);

    if (persistOnServer) {
      void this.persistLanguage(normalizedLanguage);
    }
  }

  private normalizeLanguage(
    language: string | null | undefined
  ): string | null {
    if (!language) {
      return null;
    }
    const normalizedLanguage = language.trim().toLowerCase().split('-')[0];

    return this.languages.includes(normalizedLanguage)
      ? normalizedLanguage
      : null;
  }

  private getLanguageFromUrl(url: string): string | null {
    try {
      const parsedUrl = new URL(url, this.doc.location.origin);

      return this.normalizeLanguage(parsedUrl.searchParams.get('lang'));
    } catch {
      return null;
    }
  }

  private applyLanguage(lang: string): void {
    let domain = this.doc.location.hostname;

    if (domain.includes('itbfx')) {
      const domainParts = domain.split('.');

      if (domainParts.length > 2) {
        domain = domainParts.slice(-2).join('.');
      }
    }

    this.cookieService.set('language', lang, undefined, '/', `.${domain}`);

    this.currentLang = lang;
    this.translate.use(lang);
    this.currentLang$.next(lang);

    this.doc.documentElement.lang = lang;

    const isRtl = lang === 'fa' || lang === 'ar';

    this.doc.dir = isRtl ? 'rtl' : 'ltr';
    this.direction$.next(isRtl ? 'rtl' : 'ltr');

    const customBootstrap = this.doc.getElementById(
      'customBsStyle'
    ) as HTMLLinkElement | null;

    const fontFace = this.doc.getElementById(
      'fontFace'
    ) as HTMLLinkElement | null;

    if (customBootstrap) {
      customBootstrap.href = isRtl
        ? '/assets/styles/custom-bootstrap.rtl.min.css'
        : '/assets/styles/custom-bootstrap.min.css';
    }

    if (fontFace) {
      fontFace.href = isRtl
        ? `/assets/styles/font-${lang}.css`
        : '/assets/styles/font.css';
    }

    if ((window as any).$crisp) {
      (window as any).$crisp.push(['config', 'position:reverse', isRtl]);

      (window as any).$crisp.push(['config', 'locale', lang]);
    }
  }

  private async persistLanguage(language: string): Promise<void> {
    if (!this.authService.isAuthenticated || this.app === AppsEnum.SUPPORTER) {
      return;
    }

    try {
      const currentUser =
        this.usersService.currentUser ??
        (await firstValueFrom(this.usersService.loadCurrentUser()));

      if (currentUser.language === language) {
        return;
      }

      await firstValueFrom(
        this.usersService.modifyCurrentUserProfile({
          ...currentUser,
          language,
        })
      );
    } catch (error) {
      console.error('Error modifying user language:', error);
    }
  }

  getCurrentDirection(): Observable<'rtl' | 'ltr'> {
    return this.direction$.asObservable();
  }

  getCurrentLanguage(): Observable<string> {
    return this.currentLang$.asObservable();
  }
}
